// src/utils/rdo.js
// ═══════════════════════════════════════════════════════════════════════════
// RdO — RICHIESTE D'OFFERTA AI FORNITORI (W436) — logica pura, provata a parte
// ═══════════════════════════════════════════════════════════════════════════
// Il flusso degli acquisti in tre sezioni collegate:
//   NF  (richiesta interna)  →  RdO (richiesta d'offerta, a uno o più fornitori)  →  ODA
// La RdO è facoltativa: per un articolo a listino con il fornitore abituale si fa l'ODA
// direttamente dalla NF. Per un acquisto importante si chiedono 2–3 offerte, si confrontano
// e dall'offerta scelta nasce l'ODA già con i prezzi del fornitore.
//
// Ogni RdO (raccolta «richiesteOfferta») è per UN fornitore e UNA NF:
//   { organizationId, nfId, nfNumero, commessaId, commessaNome, codiceCommessa,
//     numero ("RDO-2026-0007", assegnato all'invio: le bozze non consumano numeri),
//     fornitoreId, fornitoreNome, fornitoreEmail,
//     righe: [{ id, descrizione, codice, quantita, unitaMisura }]  (copia fissa dalla NF),
//     consegna: { luogo, data }, stato, offerta: { prezzi: {rigaId: "12,50"}, noteRighe,
//     sconto, totale, numeroOfferta, dataOfferta, consegnaGiorni, validaFino, allegato, note },
//     inviataAt/inviataDaNome/emailInviata, sollecitataAt, scelta…, motivoScarto, createdBy… }
// Stati: Da inviare → Inviata → Offerta ricevuta → Scelta | Scartata; oppure Annullata.
// I prezzi non escono mai dall'ufficio acquisti: nella richiesta al fornitore non ci sono.
// ═══════════════════════════════════════════════════════════════════════════
import { righeNfOrigine, luogoConsegnaNf } from "./acquisti";

export const STATI_RDO = ["Da inviare", "Inviata", "Offerta ricevuta", "Scelta", "Scartata", "Annullata"];
export const STATO_RDO_COLORI = {
  "Da inviare":       { bg: "#f3f4f6", text: "#6b7280" },
  "Inviata":          { bg: "#dbeafe", text: "#1d4ed8" },
  "Offerta ricevuta": { bg: "#fef3c7", text: "#b45309" },
  "Scelta":           { bg: "#d1fae5", text: "#047857" },
  "Scartata":         { bg: "#f3f4f6", text: "#9ca3af" },
  "Annullata":        { bg: "#f3f4f6", text: "#9ca3af" },
};
/** Una RdO ancora in corso (né chiusa né annullata). */
export const rdoAperta = (r) => ["Da inviare", "Inviata", "Offerta ricevuta"].includes(r?.stato);
/** Giorni passati dall'invio senza risposta (null se non è in attesa). */
export function giorniInAttesa(r, adesso = Date.now()) {
  if (r?.stato !== "Inviata") return null;
  const t = r.inviataAt?.toMillis?.() ?? (typeof r.inviataAt === "number" ? r.inviataAt : null);
  return t ? Math.max(0, Math.floor((adesso - t) / 86400000)) : null;
}
/** Dopo quanti giorni senza offerta la RdO si segnala e si propone il sollecito. */
export const GIORNI_SOLLECITO = 5;

export const numIt = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v ?? "").trim().replace(/\s|€/g, "");
  if (!s) return 0;
  const n = /,/.test(s) ? parseFloat(s.replace(/\./g, "").replace(",", ".")) : parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
const tondo = (n) => Math.round(n * 100) / 100;

/** Nuova RdO (senza numero né date: li aggiunge chi scrive) per un fornitore, dalla NF. */
export function rdoDaNf(nf, fornitore, { orgId, uid, nome } = {}) {
  return {
    organizationId: orgId || nf.organizationId || "",
    nfId: nf.id, nfNumero: nf.numero || "", nfDescrizione: String(nf.descrizione || "").trim(),
    commessaId: nf.commessaId || "", commessaNome: nf.commessaNome || "", codiceCommessa: nf.codiceCommessa || "",
    numero: "",
    fornitoreId: fornitore?.id || "", fornitoreNome: fornitore?.denominazione || "", fornitoreEmail: fornitore?.email || "",
    righe: righeNfOrigine(nf).map(r => ({ id: r.id, descrizione: r.descrizione, codice: r.codice || "", quantita: r.quantita || "1", unitaMisura: r.unitaMisura || "" })),
    consegna: { luogo: luogoConsegnaNf(nf) + (nf.destinazione === "Misto" && nf.dettaglioDestinazioneMista ? `: ${nf.dettaglioDestinazioneMista}` : ""), data: nf.dataConsegna || "" },
    stato: "Da inviare", offerta: null,
    createdBy: uid || "", createdByNome: nome || "",
  };
}

/** Totale dell'offerta (imponibile, IVA esclusa): prezzi per riga × quantità, meno lo sconto; oppure il totale scritto. */
export function totaleOfferta(rdo, offerta = rdo?.offerta) {
  if (!offerta) return 0;
  const prezzi = offerta.prezzi || {};
  const conPrezzi = (rdo?.righe || []).filter(r => String(prezzi[r.id] ?? "").trim() !== "");
  if (conPrezzi.length) {
    const lordo = conPrezzi.reduce((s, r) => s + numIt(r.quantita || 1) * numIt(prezzi[r.id]), 0);
    return tondo(lordo * (1 - numIt(offerta.sconto) / 100));
  }
  return tondo(numIt(offerta.totale));
}
/** Righe senza prezzo in un'offerta con prezzi per riga (il fornitore non le ha quotate). */
export function righeNonQuotate(rdo, offerta = rdo?.offerta) {
  const prezzi = offerta?.prezzi || {};
  const quotate = (rdo?.righe || []).filter(r => String(prezzi[r.id] ?? "").trim() !== "");
  if (!quotate.length) return [];
  return (rdo?.righe || []).filter(r => String(prezzi[r.id] ?? "").trim() === "");
}
/** Errore nel modulo dell'offerta ("" se va bene). */
export function erroreOfferta(rdo, offerta) {
  const prezzi = offerta?.prezzi || {};
  const qualchePrezzo = (rdo?.righe || []).some(r => String(prezzi[r.id] ?? "").trim() !== "");
  if (!qualchePrezzo && !(numIt(offerta?.totale) > 0)) return "Scrivi i prezzi delle righe oppure il totale dell'offerta";
  const cattivo = (rdo?.righe || []).find(r => String(prezzi[r.id] ?? "").trim() !== "" && !(numIt(prezzi[r.id]) >= 0 && /\d/.test(prezzi[r.id])));
  if (cattivo) return `Prezzo non valido per «${cattivo.descrizione}»`;
  const sc = numIt(offerta?.sconto);
  if (sc < 0 || sc >= 100) return "Lo sconto va da 0 a 99%";
  return "";
}

/**
 * Confronto delle offerte ricevute per la stessa NF: per ogni riga il prezzo di ciascun
 * fornitore e il migliore; per ogni offerta il totale, le righe non quotate e la posizione.
 * Si confrontano le offerte arrivate (ricevute, scelta e scartate), non le richieste senza risposta.
 */
export function confrontoOfferte(rdos = []) {
  // anche le scartate che hanno un'offerta: dopo la scelta il confronto resta leggibile
  const offerte = rdos.filter(r => ["Offerta ricevuta", "Scelta", "Scartata"].includes(r.stato) && r.offerta);
  const righe = offerte[0]?.righe || rdos[0]?.righe || [];
  const perRiga = righe.map(riga => {
    const prezzi = offerte.map(o => {
      const p = o.offerta?.prezzi?.[riga.id];
      return String(p ?? "").trim() === "" ? null : numIt(p) * (1 - numIt(o.offerta.sconto) / 100);
    });
    const validi = prezzi.filter(p => p !== null);
    const migliore = validi.length >= 2 ? Math.min(...validi) : null;
    return { riga, prezzi, migliore };
  });
  const totali = offerte.map(o => ({ id: o.id, totale: totaleOfferta(o), nonQuotate: righeNonQuotate(o).length }));
  const complete = totali.filter(t => t.totale > 0 && t.nonQuotate === 0).map(t => t.totale);
  const minimo = complete.length >= 2 ? Math.min(...complete) : null;
  return { offerte, perRiga, totali, idMigliore: minimo === null ? null : (totali.find(t => t.totale === minimo && t.nonQuotate === 0)?.id || null) };
}

/** Righe dell'ODA dall'offerta scelta: prezzi del fornitore, origine «da NF» per il confronto con la richiesta. */
export function righeOdaDaRdo(rdo, righeNf = []) {
  const prezzi = rdo?.offerta?.prezzi || {};
  const conPrezzi = (rdo?.righe || []).some(r => String(prezzi[r.id] ?? "").trim() !== "");
  const base = Date.now();
  return (rdo?.righe || []).map((r, i) => {
    const origine = righeNf.find(o => o.id === r.id) || { id: r.id, descrizione: r.descrizione, codice: r.codice, quantita: r.quantita, unitaMisura: r.unitaMisura };
    // senza prezzi per riga (offerta a corpo): il totale va sulla prima riga, così l'imponibile coincide
    const prezzo = conPrezzi ? String(prezzi[r.id] ?? "") : (i === 0 ? String(totaleOfferta(rdo) / Math.max(1, numIt(r.quantita || 1))).replace(".", ",") : "0");
    return { id: `r${base}_${i}`, descrizione: r.descrizione, codice: r.codice || "", quantita: r.quantita || "1", unitaMisura: r.unitaMisura || "",
      prezzoUnitario: prezzo, sconto: conPrezzi && numIt(rdo.offerta?.sconto) ? String(rdo.offerta.sconto) : "", iva: "22", daNf: origine };
  });
}

/** Note dell'ODA dall'offerta scelta. */
export function noteOdaDaRdo(rdo) {
  const o = rdo?.offerta || {};
  return [
    o.numeroOfferta ? `Rif. vostra offerta ${o.numeroOfferta}${o.dataOfferta ? ` del ${new Date(o.dataOfferta + "T00:00:00").toLocaleDateString("it-IT")}` : ""}` : "",
    rdo?.numero ? `Nostra richiesta ${rdo.numero}` : "",
    rdo?.consegna?.luogo ? `Consegna ${rdo.consegna.luogo}` : "",
  ].filter(Boolean).join(" · ");
}

/** Testo della richiesta d'offerta (per la copia e per l'anteprima): mai prezzi né dati interni. */
export function testoRichiestaOfferta(rdo, azienda = "") {
  const r = [];
  r.push(`Richiesta d'offerta${rdo?.numero ? ` ${rdo.numero}` : ""}${azienda ? ` — ${azienda}` : ""}`);
  r.push("");
  r.push(["Descrizione", "Codice", "Q.tà", "U.M."].join("\t"));
  (rdo?.righe || []).forEach(x => r.push([x.descrizione, x.codice, x.quantita, x.unitaMisura].map(v => String(v || "").replace(/[\t\n]+/g, " ")).join("\t")));
  r.push("");
  if (rdo?.consegna?.luogo) r.push(`Consegna: ${rdo.consegna.luogo}`);
  if (rdo?.consegna?.data) r.push(`Data di consegna richiesta: ${new Date(rdo.consegna.data + "T00:00:00").toLocaleDateString("it-IT")}`);
  r.push("Vi chiediamo prezzi unitari, eventuale sconto, tempi di consegna e validità dell'offerta.");
  return r.join("\n");
}

/** Riepilogo delle RdO di una NF per gli elenchi: «3 richieste · 2 offerte». */
export function riepilogoRdo(rdos = []) {
  const valide = rdos.filter(r => r.stato !== "Annullata");
  if (!valide.length) return "";
  const offerte = valide.filter(r => ["Offerta ricevuta", "Scelta"].includes(r.stato)).length;
  const scelta = valide.find(r => r.stato === "Scelta");
  return `${valide.length} richiest${valide.length === 1 ? "a" : "e"} d'offerta · ${offerte} offert${offerte === 1 ? "a" : "e"}${scelta ? ` · scelta ${scelta.fornitoreNome}` : ""}`;
}

const escH = (t) => String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
/** La stessa richiesta in HTML (tabella vera quando si incolla nell'email). */
export function htmlRichiestaOfferta(rdo, azienda = "") {
  const td = 'style="border:1px solid #999;padding:4px 8px"';
  return `<div style="font-family:Arial,sans-serif;font-size:13px"><p><b>Richiesta d'offerta${rdo?.numero ? ` ${escH(rdo.numero)}` : ""}</b>${azienda ? ` — ${escH(azienda)}` : ""}</p>
<table style="border-collapse:collapse"><tr>${["Descrizione", "Codice", "Q.tà", "U.M."].map(h => `<th ${td}>${h}</th>`).join("")}</tr>${
  (rdo?.righe || []).map(x => `<tr><td ${td}>${escH(x.descrizione)}</td><td ${td}>${escH(x.codice)}</td><td ${td}>${escH(x.quantita)}</td><td ${td}>${escH(x.unitaMisura)}</td></tr>`).join("")}</table>
${rdo?.consegna?.luogo ? `<p>Consegna: ${escH(rdo.consegna.luogo)}</p>` : ""}${rdo?.consegna?.data ? `<p>Data di consegna richiesta: ${new Date(rdo.consegna.data + "T00:00:00").toLocaleDateString("it-IT")}</p>` : ""}
<p>Vi chiediamo prezzi unitari, eventuale sconto, tempi di consegna e validità dell'offerta.</p></div>`;
}
